import React from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, TextField, Paper, Grid, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import get from 'lodash/get';

import * as API from 'api/addresses';
import InputLabel from 'components/InputLabel';
import { createAddress, deleteAddress, updateAddress } from 'store/objects/actions';
import { IP_TYPE } from 'enum/ip';

import { validationSchema } from './validationSchema';

const AddressForm = ({ object, onClose }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { cusID: customerUUID, ecoID: ecosystemUUID } = useParams();
  const nsps = useSelector((state) => get(state, 'ecosystems.currentEcosystem.nsps', []));

  const handleSave = async (values) => {
    try {
      if (object?.uuid) {
        const result = await API.updateAddress({ customerUUID, ecosystemUUID, payload: values, uuid: object.uuid });
        dispatch(updateAddress({ ecosystemUUID, result }));
        enqueueSnackbar(`${values.name} (Allocated IP) has been updated`, { variant: 'success' });
      } else {
        const result = await API.createPublicAddress({ customerUUID, ecosystemUUID, payload: values });
        dispatch(createAddress({ ecosystemUUID, result }));
        enqueueSnackbar(`${values.name} (Allocated IP) has been created`, { variant: 'success' });
      }
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error saving object:', error);
      dispatch({ type: 'ADDRESS_FAILURE', payload: { message: error } });
    }
  };

  const { values, errors, touched, handleChange, handleSubmit } = useFormik({
    initialValues: {
      name: get(object, 'name', ''),
      type: get(object, 'type', IP_TYPE.IPv4),
      description: get(object, 'description', ''),
      nsp: get(object, 'nsp', nsps[0]?.name || ''),
    },
    validationSchema,
    onSubmit: handleSave,
  });

  const handleDelete = async () => {
    if (!object?.uuid) return;
    try {
      await API.deleteAddress({ customerUUID, ecosystemUUID, uuid: object.uuid });
      dispatch(deleteAddress({ ecosystemUUID, uuid: object.uuid }));
      enqueueSnackbar(`${object.name} (Allocated IP) has been removed`, { variant: 'success' });
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error deleting object:', error);
      dispatch({ type: 'ADDRESS_DELETE_FAILURE', payload: { message: error } });
    }
  };

  return (
    <div>
      <Box sx={{ px: 4, pt: 2, pb: 3 }}>
        <Paper elevation={0} sx={{ borderRadius: 2 }}>
          <Box sx={{ p: 4 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <InputLabel required>Name</InputLabel>
                <TextField
                  fullWidth
                  name="name"
                  variant="standard"
                  placeholder="Name"
                  value={values.name}
                  onChange={handleChange}
                  error={Boolean(touched.name && errors.name)}
                  helperText={!!touched.name && errors.name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  disabled
                  label="Associated region"
                  fullWidth
                  value={nsps[0].name.includes('SJC') ? 'US West' : 'US East'}
                />
              </Grid>
              <Grid item xs={12}>
                <InputLabel>Description</InputLabel>
                <TextField
                  fullWidth
                  name="description"
                  variant="standard"
                  placeholder="Description"
                  value={values.description}
                  onChange={handleChange}
                  error={Boolean(touched.description && errors.description)}
                  helperText={!!touched.description && errors.description}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>
        <Box sx={{ pt: 3 }}>
          <Stack direction="row-reverse" justifyContent="space-between">
            {object?.uuid ? (
              <Button
                data-testid="btn-delete_allocatedip"
                variant="contained"
                color="error"
                onClick={handleDelete}
                sx={{ minWidth: 90 }}
              >
                Delete
              </Button>
            ) : (
              <Button
                data-testid="btn-save_allocatedip"
                variant="contained"
                onClick={handleSubmit}
                sx={{ minWidth: 90 }}
              >
                Add
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </div>
  );
};

AddressForm.propTypes = {
  object: PropTypes.object,
  onClose: PropTypes.func,
};

export default AddressForm;
